import React, { useState } from 'react';
import { Contact, Eye, Trash2, X, Mail, User, MessageSquare, Send, MessageSquareQuote, Clock } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './ContactUsSetting.module.css';
import { useContacts, useDeleteContact, useMarkAsReplied } from '../../../../hooks/useContactUsHooks';

function ContactUsSetting() {
  const {
    contacts,
    pagination,
    isLoading,
    error,
    refetchContacts,
    setPage,
  } = useContacts();

  // Get the mutation for marking contacts as replied
  const markAsRepliedMutation = useMarkAsReplied();
  const deleteContactMutation = useDeleteContact();


  const [showModal, setShowModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isReplying, setIsReplying] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };


  const handleDeleteInquiry = (id) => {
    if (window.confirm('Are you sure you want to delete this inquiry? This action cannot be undone.')) {
      deleteContactMutation.mutate(id, {
        onSuccess: () => {
          refetchContacts();
        }
      });
    }
  };

  const handleViewInquiry = (id) => {
    const inquiry = contacts.find(item => item.id === id);
    setSelectedInquiry(inquiry);
    setShowModal(true);
    setIsReplying(false);
    setReplyMessage('');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInquiry(null);
  };

  const handleReply = () => {
    setIsReplying(true);
  };

  const handleSendReply = () => {
    if (replyMessage.trim() === '') {
      toast.error("Reply cannot be empty!");
      return;
    }
    
    // Use the mutation to mark contact as replied and send email
    markAsRepliedMutation.mutate(
      { 
        contactId: selectedInquiry.id, 
        replyContent: replyMessage 
      },
      {
        onSuccess: () => {
          setIsReplying(false);
          setReplyMessage('');
          closeModal();
          refetchContacts(); // Refresh the contacts list
        }
      }
    );
  };

  if (isLoading) {
    return <div className={styles.loadingState}>Loading inquiries...</div>;
  }

  if (error) {
    return <div className={styles.errorState}>Error loading inquiries: {error.message}</div>;
  }

  return (
    <div className={styles.container}>
      <ToastContainer />
      <div className={styles.header}>
        <Contact className={styles.headerIcon} />
        <div>
          <h1 className={styles.title}>Contact Us Settings</h1>
          <p className={styles.subtitle}>Manage inquiries received from your contact form</p>
        </div>
      </div>

      <div className={styles.sectionContainer}>
        <h2 className={styles.sectionTitle}>Received Inquiries</h2>
        <div className={styles.tableContainer}>
          {contacts.length === 0 ? (
            <div className={styles.emptyState}>No inquiries found</div>
          ) : (
            <table className={styles.inquiryTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((inquiry) => (
                  <tr key={inquiry.id} className={inquiry.is_replied ? styles.repliedRow : ''}>
                    <td>{inquiry.name}</td>
                    <td>{inquiry.email}</td>
                    <td>{inquiry.subject}</td>
                    <td>{formatDate(inquiry.submitted_at)}</td>
                    <td>
                      <span className={inquiry.is_replied ? styles.repliedBadge : styles.pendingBadge}>
                        {inquiry.is_replied ? 'Replied' : 'Pending'}
                      </span>
                    </td>
                    <td className={styles.actionButtons}>
                      <button 
                        className={styles.viewButton}
                        onClick={() => handleViewInquiry(inquiry.id)}
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className={styles.deleteButton}
                        onClick={() => handleDeleteInquiry(inquiry.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {pagination && pagination.totalPages > 1 && (
          <div className={styles.pagination}>
            <button 
              className={styles.paginationButton} 
              disabled={pagination.currentPage === 1}
              onClick={() => setPage(pagination.currentPage - 1)}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button 
              className={styles.paginationButton} 
              disabled={pagination.currentPage === pagination.totalPages}
              onClick={() => setPage(pagination.currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showModal && selectedInquiry && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Inquiry Details</h2>
              <button className={styles.closeButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailItem}>
                <User className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Name</div>
                  <div className={styles.detailValue}>{selectedInquiry.name}</div>
                </div>
              </div>
              <div className={styles.detailItem}>
                <Mail className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Email</div>
                  <div className={styles.detailValue}>{selectedInquiry.email}</div>
                </div>
              </div>
              <div className={styles.detailItem}>
                <MessageSquare className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Subject</div>
                  <div className={styles.detailValue}>{selectedInquiry.subject}</div>
                </div>
              </div>
              <div className={styles.detailItem}>
                <MessageSquare className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Message</div>
                  <div className={styles.detailValue}>{selectedInquiry.message}</div>
                </div>
              </div>
              <div className={styles.detailItem}>
                <Clock className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Submitted</div>
                  <div className={styles.detailValue}>{formatDate(selectedInquiry.submitted_at)}</div>
                </div>
              </div>
              
              {selectedInquiry.is_replied ? (
                <div className={styles.repliedNotice}>
                  <MessageSquareQuote className={styles.detailIcon} />
                  <div>This inquiry has been marked as replied</div>
                </div>
              ) : (
                <>
                  <div className={styles.detailItem}>
                    <MessageSquareQuote className={styles.detailIcon} />
                    <div>
                      <div className={styles.detailLabel}>Reply</div>
                    </div>
                  </div>
                  {isReplying ? (
                    <textarea 
                      className={styles.replyTextArea} 
                      value={replyMessage} 
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply here..."
                    />
                  ) : null}
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              {!selectedInquiry.is_replied && (
                isReplying ? (
                  <button 
                    className={styles.replyButton} 
                    onClick={handleSendReply}
                    disabled={markAsRepliedMutation.isLoading}
                  >
                    {markAsRepliedMutation.isLoading ? 'Sending...' : (
                      <>
                        <Send size={16} /> Send
                      </>
                    )}
                  </button>
                ) : (
                  <button className={styles.replyButton} onClick={handleReply}>
                    Reply
                  </button>
                )
              )}
              <button className={styles.closeModalButton} onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactUsSetting;